/**
 * Renders the optional product-spec fields (dimensions, material, care, etc.)
 * on the product detail page as a stacked accordion. Quietly returns null
 * when every spec is empty. Empty sections are automatically skipped so the
 * panel list only contains fields the product actually has.
 *
 * Sections rendered (in this order, only if populated):
 *   - Specifications  (compact two-column table of short inline fields)
 *   - Benefits
 *   - Usage
 *   - How to Use
 *   - Care Instructions
 *   - Dos & Don'ts
 *   - FAQs
 */
import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
} from "@/components/ui/accordion";

const INLINE_FIELDS = [
    { key: "dimensions_length", label: "Dimensions — Length" },
    { key: "size", label: "Size" },
    { key: "product_weight", label: "Weight" },
    { key: "material_type", label: "Material" },
    { key: "product_life", label: "Product life" },
];

const BLOCK_FIELDS = [
    { key: "benefits", label: "Benefits" },
    { key: "usage", label: "Usage" },
    { key: "how_to_use", label: "How to Use" },
    { key: "care", label: "Care Instructions" },
    { key: "dos_and_donts", label: "Dos & Don'ts" },
    { key: "faqs", label: "FAQs" },
];

const triggerCls =
    "px-5 md:px-6 py-4 md:py-5 font-heading text-lg md:text-xl font-semibold text-ink-900 hover:no-underline hover:bg-paper-alt/40 transition-colors [&[data-state=open]>svg]:rotate-180";
const contentCls =
    "px-5 md:px-6 pb-5 md:pb-6 pt-0 text-sm md:text-base text-ink-700 leading-relaxed border-t border-gray-100";

export default function ProductSpecifications({ product }) {
    if (!product) return null;

    const inlineRows = INLINE_FIELDS.filter(
        (f) => (product[f.key] || "").trim(),
    );
    const blocks = BLOCK_FIELDS.filter((f) => (product[f.key] || "").trim());
    if (inlineRows.length === 0 && blocks.length === 0) return null;

    return (
        <Accordion
            type="multiple"
            className="mt-12 space-y-3"
            data-testid="product-specifications"
        >
            {inlineRows.length > 0 && (
                <AccordionItem
                    value="specifications"
                    className="rounded-2xl bg-white border border-gray-100 overflow-hidden border-b"
                    data-testid="spec-accordion-specifications"
                >
                    <AccordionTrigger
                        className={triggerCls}
                        data-testid="spec-trigger-specifications"
                    >
                        Specifications
                    </AccordionTrigger>
                    <AccordionContent className={contentCls}>
                        <dl className="divide-y divide-gray-100 -mx-1 mt-1">
                            {inlineRows.map((f) => (
                                <div
                                    key={f.key}
                                    className="grid grid-cols-3 gap-3 px-1 py-3 text-sm"
                                    data-testid={`spec-${f.key.replace(/_/g, "-")}`}
                                >
                                    <dt className="text-ink-700">{f.label}</dt>
                                    <dd className="col-span-2 text-ink-900 font-medium">
                                        {product[f.key]}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </AccordionContent>
                </AccordionItem>
            )}

            {blocks.map((f) => (
                <AccordionItem
                    key={f.key}
                    value={f.key}
                    className="rounded-2xl bg-white border border-gray-100 overflow-hidden border-b"
                    data-testid={`spec-accordion-${f.key.replace(/_/g, "-")}`}
                >
                    <AccordionTrigger
                        className={triggerCls}
                        data-testid={`spec-trigger-${f.key.replace(/_/g, "-")}`}
                    >
                        {f.label}
                    </AccordionTrigger>
                    <AccordionContent
                        className={contentCls}
                        data-testid={`spec-block-${f.key.replace(/_/g, "-")}`}
                    >
                        <p className="whitespace-pre-line pt-1">
                            {product[f.key]}
                        </p>
                    </AccordionContent>
                </AccordionItem>
            ))}
        </Accordion>
    );
}
